/**
 * Utility moves: how much each adds to a Pokémon's value in doubles (for automatic draft points, on a 0-10 scale) and
 * which move-search groups it belongs to. The values are judgement, checked against OgreClan's sample draft boards:
 * redirection, Spore, Fake Out and Tailwind at the top, hazards near the bottom. A value of 0 means the move is only used for
 * grouping. Pricing scales them by how well the Pokémon can use them (see league-core's pricing).
 */
export interface UtilityMove {
	value: number;
	groups: readonly string[];
}

/** The move-search groups, in the order they're shown */
export const MOVE_GROUP_NAMES = ['Fake Out', 'Speed control', 'Redirection', 'Protection', 'Hazards', 'Hazard removal', 'Pivoting', 'Recovery', 'Setup', 'Status', 'Screens', 'Support', 'Disruption', 'Weather and terrain'] as const;

export const UTILITY_MOVES: Readonly<Record<string, UtilityMove>> = {
	fakeout: { value: 8, groups: ['Fake Out'] },
	tailwind: { value: 8, groups: ['Speed control'] },
	trickroom: { value: 5, groups: ['Speed control'] },
	icywind: { value: 2.5, groups: ['Speed control'] },
	electroweb: { value: 2, groups: ['Speed control'] },
	thunderwave: { value: 3, groups: ['Speed control', 'Status'] },
	nuzzle: { value: 3, groups: ['Speed control', 'Status'] },
	glare: { value: 2.5, groups: ['Speed control', 'Status'] },
	stunspore: { value: 2, groups: ['Speed control', 'Status'] },
	bleakwindstorm: { value: 1, groups: ['Speed control'] },
	scaryface: { value: 1, groups: ['Speed control'] },
	bulldoze: { value: 1, groups: ['Speed control'] },
	rocktomb: { value: 1, groups: ['Speed control'] },
	lowsweep: { value: 0.5, groups: ['Speed control'] },
	stringshot: { value: 0.5, groups: ['Speed control'] },
	cottonspore: { value: 1, groups: ['Speed control'] },
	pounce: { value: 0.5, groups: ['Speed control'] },
	followme: { value: 10, groups: ['Redirection'] },
	ragepowder: { value: 9, groups: ['Redirection'] },
	spotlight: { value: 2, groups: ['Redirection'] },
	protect: { value: 1, groups: ['Protection'] },
	detect: { value: 1, groups: ['Protection'] },
	spikyshield: { value: 2, groups: ['Protection'] },
	kingsshield: { value: 2, groups: ['Protection'] },
	banefulbunker: { value: 1.5, groups: ['Protection'] },
	silktrap: { value: 1.5, groups: ['Protection'] },
	burningbulwark: { value: 1.5, groups: ['Protection'] },
	obstruct: { value: 1.5, groups: ['Protection'] },
	wideguard: { value: 2, groups: ['Protection'] },
	quickguard: { value: 2, groups: ['Protection'] },
	craftyshield: { value: 0.5, groups: ['Protection'] },
	matblock: { value: 1, groups: ['Protection'] },
	stealthrock: { value: 0.5, groups: ['Hazards'] },
	spikes: { value: 0.3, groups: ['Hazards'] },
	toxicspikes: { value: 0.2, groups: ['Hazards'] },
	stickyweb: { value: 0.5, groups: ['Hazards'] },
	stoneaxe: { value: 0.5, groups: ['Hazards'] },
	ceaselessedge: { value: 0.5, groups: ['Hazards'] },
	defog: { value: 0.5, groups: ['Hazard removal'] },
	rapidspin: { value: 1, groups: ['Hazard removal'] },
	mortalspin: { value: 1, groups: ['Hazard removal'] },
	courtchange: { value: 2.5, groups: ['Hazard removal'] },
	tidyup: { value: 3, groups: ['Hazard removal'] },
	uturn: { value: 2, groups: ['Pivoting'] },
	voltswitch: { value: 2, groups: ['Pivoting'] },
	flipturn: { value: 2, groups: ['Pivoting'] },
	partingshot: { value: 4.5, groups: ['Pivoting'] },
	teleport: { value: 1.5, groups: ['Pivoting'] },
	chillyreception: { value: 3, groups: ['Pivoting'] },
	shedtail: { value: 2.5, groups: ['Pivoting'] },
	batonpass: { value: 0.5, groups: ['Pivoting'] },
	recover: { value: 1, groups: ['Recovery'] },
	roost: { value: 1, groups: ['Recovery'] },
	slackoff: { value: 1, groups: ['Recovery'] },
	softboiled: { value: 1, groups: ['Recovery'] },
	milkdrink: { value: 1, groups: ['Recovery'] },
	moonlight: { value: 1, groups: ['Recovery'] },
	synthesis: { value: 1, groups: ['Recovery'] },
	morningsun: { value: 1, groups: ['Recovery'] },
	shoreup: { value: 1, groups: ['Recovery'] },
	strengthsap: { value: 1.5, groups: ['Recovery'] },
	wish: { value: 1, groups: ['Recovery'] },
	healorder: { value: 1, groups: ['Recovery'] },
	lunarblessing: { value: 1.5, groups: ['Recovery'] },
	rest: { value: 0.3, groups: ['Recovery'] },
	junglehealing: { value: 1.5, groups: ['Recovery'] },
	lifedew: { value: 2, groups: ['Recovery'] },
	swordsdance: { value: 3, groups: ['Setup'] },
	dragondance: { value: 3.5, groups: ['Setup'] },
	nastyplot: { value: 3, groups: ['Setup'] },
	calmmind: { value: 2.5, groups: ['Setup'] },
	quiverdance: { value: 4.5, groups: ['Setup'] },
	shellsmash: { value: 4.5, groups: ['Setup'] },
	bellydrum: { value: 3.5, groups: ['Setup'] },
	bulkup: { value: 2.5, groups: ['Setup'] },
	shiftgear: { value: 3.5, groups: ['Setup'] },
	victorydance: { value: 4, groups: ['Setup'] },
	coil: { value: 2.5, groups: ['Setup'] },
	geomancy: { value: 4, groups: ['Setup'] },
	tailglow: { value: 4, groups: ['Setup'] },
	agility: { value: 1.5, groups: ['Setup'] },
	rockpolish: { value: 1.5, groups: ['Setup'] },
	irondefense: { value: 1, groups: ['Setup'] },
	curse: { value: 2, groups: ['Setup'] },
	filletaway: { value: 3.5, groups: ['Setup'] },
	clangoroussoul: { value: 3.5, groups: ['Setup'] },
	noretreat: { value: 3, groups: ['Setup'] },
	growth: { value: 2, groups: ['Setup'] },
	takeheart: { value: 2.5, groups: ['Setup'] },
	workup: { value: 1, groups: ['Setup'] },
	cosmicpower: { value: 1, groups: ['Setup'] },
	amnesia: { value: 1, groups: ['Setup'] },
	autotomize: { value: 1.5, groups: ['Setup'] },
	spore: { value: 9, groups: ['Status'] },
	sleeppowder: { value: 4, groups: ['Status'] },
	hypnosis: { value: 2, groups: ['Status'] },
	yawn: { value: 1.5, groups: ['Status'] },
	willowisp: { value: 4.5, groups: ['Status'] },
	toxic: { value: 1.5, groups: ['Status'] },
	darkvoid: { value: 6, groups: ['Status'] },
	lovelykiss: { value: 3, groups: ['Status'] },
	sing: { value: 1.5, groups: ['Status'] },
	poisonpowder: { value: 0.5, groups: ['Status'] },
	toxicthread: { value: 1.5, groups: ['Status'] },
	reflect: { value: 3, groups: ['Screens'] },
	lightscreen: { value: 3, groups: ['Screens'] },
	auroraveil: { value: 3, groups: ['Screens'] },
	helpinghand: { value: 3, groups: ['Support'] },
	allyswitch: { value: 2, groups: ['Support'] },
	afteryou: { value: 3, groups: ['Support'] },
	instruct: { value: 2.5, groups: ['Support'] },
	coaching: { value: 3, groups: ['Support'] },
	decorate: { value: 2.5, groups: ['Support'] },
	pollenpuff: { value: 2, groups: ['Support'] },
	healpulse: { value: 2, groups: ['Support'] },
	healbell: { value: 0.5, groups: ['Support'] },
	aromatherapy: { value: 0.5, groups: ['Support'] },
	healingwish: { value: 3, groups: ['Support'] },
	lunardance: { value: 3, groups: ['Support'] },
	memento: { value: 2.5, groups: ['Support'] },
	trick: { value: 2, groups: ['Support'] },
	switcheroo: { value: 2, groups: ['Support'] },
	taunt: { value: 4, groups: ['Disruption'] },
	encore: { value: 4, groups: ['Disruption'] },
	disable: { value: 2.5, groups: ['Disruption'] },
	knockoff: { value: 2, groups: ['Disruption'] },
	haze: { value: 1.5, groups: ['Disruption'] },
	clearsmog: { value: 1, groups: ['Disruption'] },
	snarl: { value: 3.5, groups: ['Disruption'] },
	perishsong: { value: 3, groups: ['Disruption'] },
	imprison: { value: 2, groups: ['Disruption'] },
	quash: { value: 2, groups: ['Disruption'] },
	roar: { value: 1, groups: ['Disruption'] },
	whirlwind: { value: 1, groups: ['Disruption'] },
	dragontail: { value: 1.5, groups: ['Disruption'] },
	circlethrow: { value: 1.5, groups: ['Disruption'] },
	feint: { value: 3, groups: ['Disruption'] },
	torment: { value: 1, groups: ['Disruption'] },
	sunnyday: { value: 2, groups: ['Weather and terrain'] },
	raindance: { value: 2, groups: ['Weather and terrain'] },
	sandstorm: { value: 1, groups: ['Weather and terrain'] },
	snowscape: { value: 1.5, groups: ['Weather and terrain'] },
	hail: { value: 0.5, groups: ['Weather and terrain'] },
	electricterrain: { value: 1.5, groups: ['Weather and terrain'] },
	grassyterrain: { value: 2, groups: ['Weather and terrain'] },
	mistyterrain: { value: 1.5, groups: ['Weather and terrain'] },
	psychicterrain: { value: 1.5, groups: ['Weather and terrain'] },
	substitute: { value: 1, groups: [] },
	leechseed: { value: 1.5, groups: [] },
	guardsplit: { value: 1, groups: [] },
	powersplit: { value: 1, groups: [] },
	torchsong: { value: 0, groups: ['Setup'] },
	howl: { value: 2, groups: ['Setup'] },
	floralhealing: { value: 2, groups: ['Support'] },
	skillswap: { value: 2.5, groups: ['Support'] },
	entrainment: { value: 1.5, groups: ['Support'] },
	worryseed: { value: 1.5, groups: ['Disruption'] },
	simplebeam: { value: 1.5, groups: ['Disruption'] },
	gastroacid: { value: 1.5, groups: ['Disruption'] },
	roleplay: { value: 1, groups: ['Support'] },
	doodle: { value: 1.5, groups: ['Support'] },
	beatup: { value: 1.5, groups: ['Support'] },
	revivalblessing: { value: 8, groups: ['Support'] },
	dragoncheer: { value: 1.5, groups: ['Support'] },
	strugglebug: { value: 1.5, groups: ['Disruption'] },
	eerieimpulse: { value: 2, groups: ['Disruption'] },
	faketears: { value: 2.5, groups: ['Disruption'] },
	nobleroar: { value: 1.5, groups: ['Disruption'] },
	tickle: { value: 1.5, groups: ['Disruption'] },
	charm: { value: 1.5, groups: ['Disruption'] },
	featherdance: { value: 1.5, groups: ['Disruption'] },
	babydolleyes: { value: 1.5, groups: ['Disruption'] },
	destinybond: { value: 2.5, groups: ['Disruption'] },
};

/** group -> the ids of its moves */
export const MOVE_GROUPS: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
	MOVE_GROUP_NAMES.map(group => [group, Object.keys(UTILITY_MOVES).filter(id => UTILITY_MOVES[id]!.groups.includes(group))])
);

/** How much a move adds to a Pokémon's utility (0 if it's not a utility move) */
export function utilityValue(moveId: string) {
	return UTILITY_MOVES[moveId]?.value ?? 0;
}
